import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../apiConfig';

const MockStatisticsPage = () => {
  const [mockTrades, setMockTrades] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenPrices, setTokenPrices] = useState({});

  // Fetch mock trades from backend
  const fetchMockTrades = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
      }
      const apiUrl = getApiUrl();
      const response = await axios.get(`${apiUrl}/mock-trades`);
      setMockTrades(response.data || []);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch mock trades:', error);
      setError('Failed to fetch mock trades');
      setMockTrades([]);
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  // Filter trades by time
  const getFilteredTrades = () => {
    const now = new Date();
    const filterTime = new Date();
    
    switch (timeFilter) {
      case '1h':
        filterTime.setHours(now.getHours() - 1);
        break;
      case '6h':
        filterTime.setHours(now.getHours() - 6);
        break;
      case '24h':
        filterTime.setDate(now.getDate() - 1);
        break;
      case '7d':
        filterTime.setDate(now.getDate() - 7);
        break;
      case '30d':
        filterTime.setDate(now.getDate() - 30);
        break;
      default:
        return mockTrades;
    }
    
    return mockTrades.filter(trade => {
      const tradeTime = new Date(trade.buy_time || trade.created_at);
      return tradeTime >= filterTime;
    });
  };

  const filteredTrades = getFilteredTrades();

  // Helper function for holding percentage
  const getHoldingPercentage = (trade) => {
    if (!trade.buy_time) return 0;
    if (!trade.original_amount || trade.original_amount <= 0) {
      return 100;
    }
    const currentAmount = trade.amount || trade.original_amount || 0;
    return Math.round((currentAmount / trade.original_amount) * 100);
  };

  // Helper function to check if trade has buy transaction icon link
  const hasBuyTransactionIcon = (trade) => {
    return !!trade.transaction_id && !!trade.buy_time;
  };

  // Fetch token price from DexScreener
  const fetchTokenPrice = async (tokenAddress) => {
    try {
      const response = await axios.get(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`);
      if (response.data && response.data.pairs && response.data.pairs.length > 0) {
        const pair = response.data.pairs[0];
        return parseFloat(pair.priceUsd) || 0;
      }
      return 0;
    } catch (error) {
      console.error(`Failed to fetch price for token ${tokenAddress}:`, error);
      return 0;
    }
  };

  // Fetch prices for all active mock trades
  const fetchTokenPrices = async () => {
    const activeTrades = mockTrades.filter(trade => 
      hasBuyTransactionIcon(trade) && !trade.sell_time && trade.token_address
    );
    
    const pricePromises = activeTrades.map(async (trade) => {
      const price = await fetchTokenPrice(trade.token_address);
      return { tokenAddress: trade.token_address, price };
    });
    
    try {
      const prices = await Promise.all(pricePromises);
      const priceMap = {};
      prices.forEach(({ tokenAddress, price }) => {
        priceMap[tokenAddress] = price;
      });
      setTokenPrices(priceMap);
    } catch (error) {
      console.error('Failed to fetch token prices:', error);
    }
  };

  // Helper function to calculate P/L with fallback to current price calculation
  const calculateProfitLoss = (trade) => {
    let profitLoss = trade.profit_loss;
    
    // For completed trades (with sell_time), always use the stored profit_loss
    if (trade.sell_time) {
      return profitLoss || 0;
    }
    
    // For active trades only: If profit_loss is 0, null, or undefined, calculate it from current price vs buy price
    if ((profitLoss === 0 || profitLoss === null || profitLoss === undefined) && 
        trade.buy_price && trade.buy_price > 0) {
      
      // Try to get real-time price from tokenPrices state
      const realTimePrice = tokenPrices[trade.token_address];
      
      // Use real-time price if available, otherwise use current_price, then buy_price as fallback
      const currentPrice = realTimePrice || trade.current_price || trade.buy_price;
      
      // Calculate P/L if we have a different price than buy price
      if (currentPrice !== trade.buy_price && currentPrice > 0) {
        profitLoss = ((currentPrice - trade.buy_price) / trade.buy_price) * 100;
      } else {
        // If prices are the same or invalid, P/L is 0
        profitLoss = 0;
      }
    }
    
    return profitLoss || 0;
  };

  // Calculate statistics
  const totalTrades = filteredTrades.length;
  const completedTrades = filteredTrades.filter(trade => hasBuyTransactionIcon(trade) && (getHoldingPercentage(trade) === 0 || trade.sell_time)).length;
  const activeTrades = filteredTrades.filter(trade => hasBuyTransactionIcon(trade) && !trade.sell_time).length;
  const nothingTrades = filteredTrades.filter(trade => !hasBuyTransactionIcon(trade)).length;
  const failedTrades = filteredTrades.filter(trade => 
    (trade.status === 'failed' || trade.failed) || 
    (trade.buy_time && !hasBuyTransactionIcon(trade)) || 
    (trade.transaction_warning && trade.transaction_warning.includes('failed'))
  ).length;
  
  const profitableTrades = filteredTrades.filter(trade => calculateProfitLoss(trade) > 0).length;
  const lossTrades = filteredTrades.filter(trade => calculateProfitLoss(trade) < 0).length;
  
  const totalProfit = filteredTrades
    .filter(trade => calculateProfitLoss(trade) > 0)
    .reduce((sum, trade) => sum + calculateProfitLoss(trade), 0);
  
  const totalLoss = filteredTrades
    .filter(trade => calculateProfitLoss(trade) < 0)
    .reduce((sum, trade) => sum + Math.abs(calculateProfitLoss(trade)), 0);
  
  const winRate = completedTrades > 0 ? (profitableTrades / completedTrades) * 100 : 0;

  // Calculate mock-specific statistics
  const mockTradesCount = filteredTrades.filter(trade => trade.is_mock_transaction).length;
  const avgProfitPerTrade = completedTrades > 0 ? (totalProfit - totalLoss) / completedTrades : 0;
  const bestTrade = filteredTrades.reduce((best, trade) => {
    if (trade.profit_loss > (best?.profit_loss || -Infinity)) {
      return trade;
    }
    return best;
  }, null);
  const worstTrade = filteredTrades.reduce((worst, trade) => {
    if (trade.profit_loss < (worst?.profit_loss || Infinity)) {
      return trade;
    }
    return worst;
  }, null);

  // Fetch data on component mount
  useEffect(() => {
    fetchMockTrades(true); // Initial load
    
    // Set up periodic refresh
    const interval = setInterval(() => {
      fetchMockTrades(false); // Background refresh
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch token prices when mock trades change
  useEffect(() => {
    if (mockTrades.length > 0) {
      fetchTokenPrices();
    }
  }, [mockTrades]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-2 border-transparent" style={{ borderTopColor: '#22c55e', borderRightColor: '#22c55e' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 text-lg">{error}</p>
        <button 
          onClick={fetchMockTrades}
          className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-white mb-2">Mock Trading Statistics & Analytics</h1>
        <p className="text-slate-400">Comprehensive overview of your mock trading performance</p>
        <div className="mt-4">
          <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-400">
            MOCK TRADING SIMULATION
          </span>
        </div>
      </div>

      {/* Mock Trading Info Banner */}
      <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <span className="text-yellow-400 text-lg">📊</span>
          <div>
            <h4 className="text-yellow-400 font-semibold">Mock Trading Analytics</h4>
            <p className="text-yellow-300 text-sm">
              These statistics are based on simulated trades with no real financial risk. 
              Use this data to analyze the potential profitability of your trading strategy.
            </p>
            <p className="text-yellow-200 text-xs mt-1">
              All profits and losses are simulated - no actual SOL was spent or earned.
            </p>
          </div>
        </div>
      </div>

      {/* Time Filter */}
      <div className="flex justify-center">
        <div className="backdrop-blur-sm rounded-lg p-2" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
          <div className="flex gap-3">
            {['all', '1h', '6h', '24h', '7d', '30d'].map((filter) => (
              <button
                key={filter}
                onClick={() => setTimeFilter(filter)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeFilter === filter
                    ? 'text-white'
                    : 'text-slate-300 hover:text-white'
                }`}
                style={timeFilter === filter ? { backgroundColor: '#22c55e' } : { backgroundColor: '#353535' }}
                onMouseEnter={(e) => { if (timeFilter !== filter) e.target.style.backgroundColor = '#22c55e'; }}
                onMouseLeave={(e) => { if (timeFilter !== filter) e.target.style.backgroundColor = '#353535'; }}
              >
                {filter === 'all' ? 'All Time' : filter === '1h' ? '1 Hour' : filter === '6h' ? '6 Hours' : filter === '24h' ? '24 Hours' : filter === '7d' ? '7 Days' : '30 Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mock Trading Statistics */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Mock Trading Statistics</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Mock Trades</div>
            <div className="text-2xl font-bold text-white">{totalTrades}</div>
            <div className="text-xs text-slate-500 mt-1">
              {timeFilter === 'all' ? 'All Time' : `Last ${timeFilter}`}
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Completed Mock Trades</div>
            <div className="text-2xl font-bold text-green-400">{completedTrades}</div>
            <div className="text-xs text-slate-500 mt-1">
              {totalTrades > 0 ? `${((completedTrades / totalTrades) * 100).toFixed(1)}%` : '0%'}
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Active Mock Trades</div>
            <div className="text-2xl font-bold text-purple-400">{activeTrades}</div>
            <div className="text-xs text-slate-500 mt-1">Currently Simulating</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Failed Mock Trades</div>
            <div className="text-2xl font-bold text-red-400">{failedTrades}</div>
            <div className="text-xs text-slate-500 mt-1">
              {totalTrades > 0 ? `${((failedTrades / totalTrades) * 100).toFixed(1)}%` : '0%'}
            </div>
          </div>
        </div>

        {/* Mock Profit/Loss Analysis */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Mock Win Rate</div>
            <div className="text-3xl font-bold text-green-400">{winRate.toFixed(1)}%</div>
            <div className="text-xs text-slate-500 mt-1">
              {profitableTrades} wins / {lossTrades} losses
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Mock Profit</div>
            <div className="text-3xl font-bold text-green-400">
              +${totalProfit.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">From Profitable Mock Trades</div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4 text-center" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="text-sm text-slate-400 mb-2">Total Mock Loss</div>
            <div className="text-3xl font-bold text-red-400">
              -${totalLoss.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">From Loss-Making Mock Trades</div>
          </div>
        </div>

        {/* Mock Net P&L */}
        <div className="mt-6 rounded-lg p-4 text-center" style={{ backgroundColor: '#0f100f', border: '1px solid white' }}>
          <div className="text-sm text-slate-400 mb-2">Mock Net Profit/Loss</div>
          <div className={`text-4xl font-bold ${totalProfit - totalLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {totalProfit - totalLoss >= 0 ? '+' : ''}${(totalProfit - totalLoss).toFixed(2)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Overall Mock Trading Performance (Simulated)
          </div>
        </div>
      </div>

      {/* Mock Trading Performance Analysis */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Mock Trading Performance Analysis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Average Performance</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Avg Profit per Trade:</span>
                <span className={`font-semibold ${avgProfitPerTrade >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {avgProfitPerTrade >= 0 ? '+' : ''}${avgProfitPerTrade.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Mock Trades:</span>
                <span className="text-yellow-400 font-semibold">{mockTradesCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Success Rate:</span>
                <span className={`font-semibold ${winRate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                  {winRate.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Best Mock Trade</h3>
            {bestTrade ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Token:</span>
                  <span className="text-blue-400 font-semibold">{bestTrade.symbol || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Profit:</span>
                  <span className="text-green-400 font-semibold">+{bestTrade.profit_loss?.toFixed(2) || '0'}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Amount:</span>
                  <span className="text-white font-semibold">{bestTrade.amount || '0'} SOL</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center">No completed trades yet</p>
            )}
          </div>

          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Worst Mock Trade</h3>
            {worstTrade ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Token:</span>
                  <span className="text-blue-400 font-semibold">{worstTrade.symbol || 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Loss:</span>
                  <span className="text-red-400 font-semibold">{worstTrade.profit_loss?.toFixed(2) || '0'}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Amount:</span>
                  <span className="text-white font-semibold">{worstTrade.amount || '0'} SOL</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-400 text-center">No completed trades yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Mock Trade Status Breakdown */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Mock Trade Status Breakdown</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400">Active Mock Trades</span>
              <span className="text-purple-400 font-bold">{activeTrades}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full" 
                style={{ width: `${totalTrades > 0 ? (activeTrades / totalTrades) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400">Completed Mock Trades</span>
              <span className="text-green-400 font-bold">{completedTrades}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${totalTrades > 0 ? (completedTrades / totalTrades) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
          
          <div className="backdrop-blur-sm rounded-lg p-4" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400">Failed Mock Trades</span>
              <span className="text-red-400 font-bold">{failedTrades}</span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full" 
                style={{ width: `${totalTrades > 0 ? (failedTrades / totalTrades) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Mock Trading Strategy Insights */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6">Mock Trading Strategy Insights</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="backdrop-blur-sm rounded-lg p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Strategy Performance</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Risk Level:</span>
                <span className={`font-semibold ${
                  winRate >= 70 ? 'text-green-400' : 
                  winRate >= 50 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {winRate >= 70 ? 'Low Risk' : winRate >= 50 ? 'Medium Risk' : 'High Risk'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Profitability:</span>
                <span className={`font-semibold ${totalProfit - totalLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalProfit - totalLoss >= 0 ? 'Profitable' : 'Loss-Making'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Trade Frequency:</span>
                <span className="text-blue-400 font-semibold">
                  {totalTrades > 0 ? `${(totalTrades / Math.max(1, (Date.now() - new Date(filteredTrades[0]?.buy_time || Date.now()).getTime()) / (1000 * 60 * 60 * 24))).toFixed(1)} trades/day` : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="backdrop-blur-sm rounded-lg p-6" style={{ background: 'linear-gradient(135deg, #191919, #191919)', border: '1px solid white' }}>
            <h3 className="text-lg font-semibold text-white mb-4">Recommendations</h3>
            <div className="space-y-3">
              {winRate >= 70 ? (
                <p className="text-green-400 text-sm">
                  ✅ Your mock strategy shows excellent performance. Consider going live with small amounts.
                </p>
              ) : winRate >= 50 ? (
                <p className="text-yellow-400 text-sm">
                  ⚠️ Your mock strategy shows moderate performance. Consider refining your entry/exit criteria.
                </p>
              ) : (
                <p className="text-red-400 text-sm">
                  ❌ Your mock strategy needs improvement. Analyze failed trades and adjust parameters.
                </p>
              )}
              
              {totalProfit - totalLoss >= 0 ? (
                <p className="text-green-400 text-sm">
                  💰 Your mock strategy is profitable. Monitor for consistency over time.
                </p>
              ) : (
                <p className="text-red-400 text-sm">
                  📉 Your mock strategy is losing money. Review stop-loss and take-profit settings.
                </p>
              )}
              
              <p className="text-blue-400 text-sm">
                🔄 Continue mock trading to gather more data before making live trades.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockStatisticsPage;
